import * as Sentry from '@sentry/react';
import { redactCardSecrets } from '@/shared/utils/redact-card-secrets';

export function initSentry() {
  const isDev = import.meta.env.DEV;
  const tunnelUrl = isDev ? '/sentry-tunnel' : '/api/sentry-tunnel';

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN || 'https://3e5bbc34ecdaf5ce3dba51a52340bce5@o4510087017398272.ingest.us.sentry.io/4510150495305728',
    tunnel: tunnelUrl,
    environment: import.meta.env.MODE || 'production',
    release: `pratigo-app@${import.meta.env.VITE_APP_VERSION || '0.0.1'}`,
    debug: isDev,
    tracesSampleRate: 1.0,
    profilesSampleRate: 1.0,
    replaysSessionSampleRate: isDev ? 1.0 : 0.1,
    replaysOnErrorSampleRate: 1.0,
    tracePropagationTargets: ['localhost', /^https?:\/\/(api\.)?pratigo\.com\.br/, /^http:\/\/localhost:\d+/],

    integrations: [
      Sentry.browserTracingIntegration({
        enableInp: true,
        traceFetch: true,
        traceXHR: true,
        enableLongAnimationFrame: true,
      }),
      Sentry.browserProfilingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
        maskAllInputs: true,
        networkDetailAllowUrls: [/pratigo\.com\.br/],
        networkCaptureBodies: true,
        // PRAT-79: o place-order pode levar o CVV do cartão salvo no corpo.
        beforeAddRecordingEvent: event => redactCardSecrets(event),
      }),
      Sentry.captureConsoleIntegration({ levels: ['error', 'warn'] }),
      Sentry.httpClientIntegration({ failedRequestStatusCodes: [[400, 599]] }),
      Sentry.contextLinesIntegration(),
      Sentry.linkedErrorsIntegration({ key: 'cause', limit: 5 }),
      Sentry.moduleMetadataIntegration(),
      Sentry.reportingObserverIntegration(),
    ],

    beforeSend(event, hint) {
      const error = hint.originalException;
      if (error instanceof Error) {
        event.extra = { ...event.extra, errorName: error.name, errorMessage: error.message };
      }
      return event;
    },

    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.category === 'console') {
        const message = breadcrumb.message?.toLowerCase() || '';
        if (message.includes('password') || message.includes('token') || message.includes('secret')) {
          return null;
        }
      }
      if (breadcrumb.category === 'fetch' || breadcrumb.category === 'xhr') {
        const url = breadcrumb.data?.url?.toLowerCase() || '';
        if (url.includes('password') || url.includes('token')) {
          breadcrumb.data = { ...breadcrumb.data, url: '[FILTERED]' };
        }
      }
      return breadcrumb;
    },

    ignoreErrors: [
      'Non-Error promise rejection captured',
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'NetworkError',
      'Network request failed',
      'Failed to fetch',
      'Load failed',
      /chrome-extension/i,
      /moz-extension/i,
      /safari-extension/i,
    ],

    ignoreTransactions: [/^\/sentry-tunnel$/],
    denyUrls: [/extensions\//i, /^chrome:\/\//i, /^moz-extension:\/\//i],
  });

  Sentry.setContext('device', {
    userAgent: navigator.userAgent,
    language: navigator.language,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });

  Sentry.setTag('app.version', import.meta.env.VITE_APP_VERSION || '0.0.1');
}

export function captureException(error: Error, context?: Record<string, unknown>) {
  Sentry.captureException(error, { contexts: { custom: context } });
}

export function captureMessage(message: string, level: Sentry.SeverityLevel = 'info') {
  Sentry.captureMessage(message, level);
}

export function addBreadcrumb(message: string, category: string, level: Sentry.SeverityLevel = 'info', data?: Record<string, unknown>) {
  Sentry.addBreadcrumb({ message, category, level, data, timestamp: Date.now() / 1000 });
}

export function setUser(user: { id: string; email?: string; username?: string; name?: string }) {
  Sentry.setUser(user);
}

export function clearUser() {
  Sentry.setUser(null);
}

export function setTag(key: string, value: string) {
  Sentry.setTag(key, value);
}

export function setContext(name: string, context: Record<string, unknown>) {
  Sentry.setContext(name, context);
}

export function startTransaction(name: string, op: string) {
  return Sentry.startSpan({ name, op }, (span) => span);
}


export { Sentry };
